import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CircularProgress, FormControl, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import { PromoRole } from '~/services/PromoService';

interface RoleOption {
  label: string;
  value: string;
}

interface Props {
  id: string;
  required?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  label?: string;
  selectedRoleId?: string;
  onChange?: (roleId: string) => void;
  loading?: boolean;
  formErrors?: Array<string>;
  clearOnBlur?: boolean;
  promoRoles: Array<PromoRole> | null;
}

function PromoRoleSelector({
  id = 'promo-role-selector',
  required = false,
  readonly = false,
  disabled = false,
  label = 'Select Promo Role',
  selectedRoleId,
  onChange,
  loading = false,
  formErrors = [],
  clearOnBlur = false,
  promoRoles: passedPromoRoles,
}: Props) {
  const [roleSuggestions, setRoleSuggestions] = useState<Array<PromoRole>>([]);
  const [selectedRole, setSelectedRole] = useState<RoleOption | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const effectiveRoles = passedPromoRoles || roleSuggestions;

  useEffect(() => {
    if (selectedRoleId && effectiveRoles.length > 0) {
      const role = effectiveRoles.find((r) => r.roleId === selectedRoleId);
      if (role) {
        setSelectedRole({
          label: role.roleName,
          value: role.roleId,
        });
      }
    } else if (!selectedRoleId) {
      setSelectedRole(null);
    }
  }, [selectedRoleId, effectiveRoles]);

  const roleOptions = useMemo<Array<RoleOption>>(
    () =>
      effectiveRoles.map((role) => ({
        label: role.roleName,
        value: role.roleId,
      })),
    [effectiveRoles],
  );

  const handleRoleChange = useCallback(
    (_, newOption: RoleOption | null) => {
      setSelectedRole(newOption);
      if (onChange) {
        onChange(newOption?.value ?? '');
      }
    },
    [onChange],
  );

  return (
    <FormControl error={formErrors?.includes(id)} sx={{ minWidth: 240 }} fullWidth>
      <Autocomplete
        id={id}
        clearOnBlur={clearOnBlur}
        multiple={false}
        disabled={disabled}
        readOnly={readonly}
        options={roleOptions}
        loading={loading}
        loadingText="Loading promo roles..."
        value={selectedRole}
        getOptionLabel={(option: RoleOption) => option?.label ?? option.toString()}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={(options, params) => {
          const allOptions = options.concat({
            label: '-- No role required --',
            value: '',
          });
          if (passedPromoRoles) {
            const filtered = allOptions.filter((option) =>
              option.label.toLowerCase().includes(params.inputValue.toLowerCase()),
            );
            return filtered;
          }
          return allOptions;
        }}
        noOptionsText="No promo roles found"
        onChange={handleRoleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            name={id}
            required={required}
            label={label}
            fullWidth
            data-testid="PromoRoleSelector"
            error={formErrors?.includes(id)}
            helperText={formErrors?.includes(id) ? 'Please select a promo role from the dropdown' : undefined}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        slotProps={{ paper: { elevation: 1 } }}
      />
    </FormControl>
  );
}

export default PromoRoleSelector;
